/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { PropTypes } from 'prop-types';
import FormField from '../forms/FormBuilder/FormFields/FormField';

import BoardDates from './BoardDates';
import BoardText from './BoardText';

import { parseDateField } from './boards.helpers';

import { startsWithDollarSign } from '../forms/ResponderHelpers';
import { formatProjectLabelsFromDropdownValues } from '../projects/projectHelpers';
import { formatEquipmentLabelsFromDropdownValues } from '../equipment/equipment.helpers';

const boardRenderedTypes = new Set([
  'dateRange',
  'dateTime',
  'text',
  'attribute',
  'calculation',
  'dropdown',
  'yes-no',
]);

/**
 * Dynamic attributes mirror a field on a linked entity,
 * so they render as that field's type.
 */
const getLinkedFieldType = ({ selectedType, configProps = {} }) => {
  if (selectedType !== 'dynamicAttribute') return selectedType;

  const linkedType = configProps.fieldType;
  return boardRenderedTypes.has(linkedType) ? linkedType : selectedType;
};

export default function BoardField({
  index,
  field = {},
  showCondensedView = false,
  projectIdMap,
  equipmentIdMap,
  settings,
}) {
  const { id, configProps = {} } = field;
  const {
    response = {},
  } = field;
  const selectedType = getLinkedFieldType(field);
  const { title, isCurrency } = configProps;
  const fullField = {
    ...field,
    selectedType,
    configProps: { ...configProps, optional: true },
  };

  const formattedTitle = showCondensedView ? `${title}: ` : title;

  switch (selectedType) {
    case 'dateRange': {
      return (
        <BoardDates
          {...field}
          showCondensedView={showCondensedView}
          valueStyle={{
            fontSize: 14,
            paddingLeft: 5,
            minHeight: 22,
          }}
          titleStyle={{
            textDecoration: 'underline',
          }}
        />
      );
    }
    case 'dateTime': {
      return (
        <BoardText
          value={parseDateField(response, configProps)}
          title={formattedTitle}
          showCondensedView={showCondensedView}
          valueStyle={{
            fontSize: 14,
            paddingLeft: 5,
            minHeight: 22,
          }}
          titleStyle={{
            textDecoration: 'underline',
          }}
        />
      );
    }
    case 'text':
    case 'attribute':
    case 'calculation': {
      const { value = '' } = response;
      const prefix = isCurrency && !startsWithDollarSign(value) ? '$ ' : '';
      return (
        <BoardText
          value={`${prefix}${value}`}
          title={formattedTitle}
          showCondensedView={showCondensedView}
          valueStyle={{
            fontSize: 14,
            paddingLeft: 5,
            minHeight: 22,
          }}
          titleStyle={{
            textDecoration: 'underline',
          }}
        />
      );
    }
    case 'dropdown': {
      const { values = [] } = response;
      const dropdownDataType = configProps.fieldDataType ?? configProps.dataType;

      let value;

      if (dropdownDataType === 'Equipment') {
        value = formatEquipmentLabelsFromDropdownValues({ values, equipmentIdMap, settings })?.join(', ');
      } else if (dropdownDataType === 'Projects') {
        value = formatProjectLabelsFromDropdownValues({ values, projectIdMap, settings })?.join(', ');
      } else {
        value = values.reduce((acc, val) => {
          if (val.name) return `${acc}${acc.length > 0 ? ', ' : ''}${val.name}`;
          return acc;
        }, '');
      }

      return (
        <BoardText
          value={value}
          title={formattedTitle}
          showCondensedView={showCondensedView}
          valueStyle={{
            fontSize: 14,
            paddingLeft: 5,
            minHeight: 22,
          }}
          titleStyle={{
            textDecoration: 'underline',
          }}
        />
      );
    }
    case 'yes-no': {
      const { value, explanation } = response;
      let ans;
      // value === undefined if user didnt fill out the field
      if (value === true || value === 'yes') {
        ans = 'Yes';
      } else if (value === false || value === 'no') {
        ans = 'No';
      } else if (value === null || value === 'n/a') {
        ans = 'N/A';
      }
      if (explanation) ans += `. ${explanation}`;
      return (
        <BoardText
          value={ans}
          title={formattedTitle}
          showCondensedView={showCondensedView}
          valueStyle={{
            fontSize: 14,
            paddingLeft: 5,
            minHeight: 22,
          }}
          titleStyle={{
            textDecoration: 'underline',
          }}
        />
      );
    }
    default:
      return (
        <FormField
          key={id}
          sectionId={id}
          index={index}
          field={fullField}
          isDisplay
          draggable={false}
          showCondensedView={showCondensedView}
        />
      );
  }
}

BoardField.propTypes = {
  index: PropTypes.number.isRequired,
  field: PropTypes.shape({}),
  showCondensedView: PropTypes.bool,
  projectIdMap: PropTypes.shape({}),
  equipmentIdMap: PropTypes.shape({}),
  settings: PropTypes.shape({}),
};

BoardField.defaultProps = {
  field: {},
  showCondensedView: false,
  projectIdMap: {},
  equipmentIdMap: {},
  settings: {},
};
